import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '../../store/useEditorStore';
import { useProjectStore } from '../../store/useProjectStore';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../utils/request';
import { loadProjectFromUrl } from '../../utils/fileManager';
import { Project } from '../../types';
import TopBar from '../../components/TopBar';
import Toolbar from '../../components/Toolbar';
import Canvas from '../../components/Canvas';
import RightPanel from '../../components/RightPanel';
import LoadingOverlay from '../../components/LoadingOverlay';
import PreviewModal from '../../components/PreviewModal';
import GuidedTour from '../../components/GuidedTour/GuidedTour';
import { captureGrowthAttribution, trackGrowthEvent } from '../../utils/growthTracking';

interface Quota {
  remainingMaps: number;
}

interface EditorPageProps {
  isExampleRoute?: boolean;
}

export default function EditorPage({ isExampleRoute = false }: EditorPageProps) {
  const { mapId: routeMapId } = useParams<{ mapId: string }>();
  const mapId = isExampleRoute ? 'example' : routeMapId;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loadProject, createNewProject, project, guidedTourDismissed, guidedTourCompleted, setGuidedTourEnabled, baseMapCentered } = useEditorStore();
  const { getProject, setCurrentProjectId } = useProjectStore();
  const { quota, updateQuota } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const [mapsBlocked, setMapsBlocked] = useState(false);

  useEffect(() => {
    const checkMapsPermission = async () => {
      if (mapId === 'example') return;

      const isExistingMap = mapId && mapId !== 'example';
      if (isExistingMap) return;

      const remaining = quota?.remainingMaps ?? 0;
      if (remaining <= 0) {
        setMapsBlocked(true);
        return;
      }
      if (!quota) {
        try {
          const res = await api.get<{ quota: Quota }>('/user/subscription/current');
          const q = res.quota;
          updateQuota(q as any);
          if ((q as any)?.remainingMaps <= 0) {
            setMapsBlocked(true);
            return;
          }
        } catch {
          // 网络错误时允许进入
        }
      }
    };
    checkMapsPermission();
  }, []);

  // 自动触发引导逻辑 - 上传底图并校准后，如果没有绘制区域，显示引导
  useEffect(() => {
    if (!project) return;

    const hasBaseMap = !!project.mapConfig?.baseMap;
    const isCalibrated = project.mapConfig?.isCalibrated;
    const hasValidArea = !!project.mapConfig?.validArea && project.mapConfig.validArea.length >= 3;

    // 条件满足时自动开启引导（需等待底图居中完成）
    if (hasBaseMap && isCalibrated && !hasValidArea && !guidedTourDismissed && !guidedTourCompleted && baseMapCentered) {
      const timer = setTimeout(() => {
        setGuidedTourEnabled(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [project, baseMapCentered, guidedTourDismissed, guidedTourCompleted, setGuidedTourEnabled]);

  useEffect(() => {
    const initProject = async () => {
      if (mapId === 'example') {
        captureGrowthAttribution();
        sessionStorage.setItem('isExampleMode', 'true');
        setIsLoading(true);

        try {
          const projectData = await loadProjectFromUrl('/example/example.kimap.tm');
          loadProject(projectData.data as Project);
          trackGrowthEvent('example_loaded', '/editor/example');
          message.success({ content: t('topBar.loadSuccess', { name: projectData.name }), key: 'loading' });
        } catch (error) {
          console.error('Failed to load example project:', error);
          message.error({ content: t('topBar.loadFailed'), key: 'loading' });
          sessionStorage.removeItem('isExampleMode');
          navigate('/maps');
        } finally {
          setIsLoading(false);
        }
      } else if (mapId) {
        setIsLoading(true);
        try {
          // 从后端获取地图信息（包括 mapName）
          // api.get 返回 res.data，即 { id, mapName, ... }
          const mapInfo = await api.get<any>(`/maps/${mapId}`);
          console.log('Map info response:', mapInfo);

          // mapInfo 已经是地图数据对象
          const mapName = mapInfo?.mapName || mapInfo?.name || mapInfo?.title || `Map`;
          console.log('Using map name:', mapName);

          const savedProject = getProject(mapId);
          if (savedProject && savedProject.data) {
            // 如果项目有数据，加载项目数据并更新名称
            setCurrentProjectId(mapId);
            const projectData = savedProject.data as Project;
            loadProject({ ...projectData, name: mapName });
          } else {
            // 如果没有项目数据，创建新项目并使用地图名称
            setCurrentProjectId(mapId);
            createNewProject(mapName);
          }
        } catch (error) {
          console.error('Failed to load map info:', error);
          // 如果获取失败，从 URL 提取地图 ID 的最后部分作为名称
          const fallbackName = mapId.length > 8 ? mapId.substring(mapId.length - 8) : mapId;
          setCurrentProjectId(mapId);
          createNewProject(`Map`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    initProject();

    return () => {
      sessionStorage.removeItem('isExampleMode');
    };
  }, [mapId]);

  const handleBackToList = () => {
    navigate('/maps');
  };

  const handleUpgrade = () => {
    navigate('/subscription');
  };

  const isExample = mapId === 'example';

  if (mapsBlocked) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('editor.permissionDenied') || '无地图权限'}</h2>
          <p className="text-gray-500 mb-6">{t('editor.permissionDeniedDesc') || '您的地图配额已用完，请升级套餐继续使用地图绘制功能。'}</p>
          <button
            onClick={handleUpgrade}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            {t('editor.upgradePlan') || '升级套餐'}
          </button>
          <div className="mt-3">
            <button onClick={handleBackToList} className="text-sm text-gray-400 hover:text-gray-600">
              {t('editor.backToMaps') || '返回地图列表'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden relative">
      <TopBar onBackToList={handleBackToList} isExampleProject={isExample} />
      <div className="flex-1 flex overflow-hidden">
        <Toolbar />
        <Canvas />
        <RightPanel />
      </div>
      {isLoading && (
        <LoadingOverlay
          text={t('app.loadingProject')}
          bgStyle="white"
        />
      )}
      {/* 3D预览弹窗 */}
      <PreviewModal />

      {/* 步骤指引引导 */}
      <GuidedTour
        visible={useEditorStore.getState().isGuidedTourEnabled}
        onClose={() => setGuidedTourEnabled(false)}
      />
    </div>
  );
}
